/**
 * /ndt-inspection/jobs/:id — one NDT job (GET /ndt-inspection/jobs/:id), its
 * crew, its welds and its repair rate. fieldiq-api#1144.
 *
 * STATUS: scheduled → active → complete; scheduled | active → cancelled with a
 * reason. Complete and cancelled are terminal. The API decides every move and
 * refuses with a code (no_welds, welds_unresolved, job_closed, …), shown here
 * as a plain sentence. "Complete" is disabled, with the reason beside it,
 * while the job's own weld counts show welds unexamined, pending or awaiting
 * repair (ndtCompleteBlocker — the API's three buckets); the API's refusal
 * stays the authority if the counts are stale.
 *
 * The legal-location field reads "LSD" on a Canadian job and "County / legal
 * description" on a US one (ndtLocationRefLabel, from the job's province/state).
 * The repair rate shows once, in the Quality card.
 *
 * Its inspection reports (batch 3, fieldiq-api#1152) sit under the welds:
 * New report, and a link to the final once one exists (NdtReportBits).
 *
 * Jobs and crew are written by NDT_DISPATCH roles; welds by NDT_WORK (which
 * adds field_worker — the API then checks the crew). Buttons a role cannot
 * press are left off, not greyed.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Ban, CheckCircle2, ClipboardList, Pencil, Play, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDeleteNdtJob, useNdtJob, useNdtJobWelds, useRemoveNdtCrew, useUpdateNdtJob } from '../../hooks/useNdtInspection';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canDispatchNdt,
  canWorkNdt,
  ndtCompleteBlocker,
  ndtLocationRefLabel,
  formatNdtValue,
  NDT_TICKET_MODE_LABEL,
  ndtJobStatusSkin,
  ndtRefusal,
  ndtWeldStandingSkin,
  type NdtRefusal,
} from '../../lib/ndt-inspection';
import type { NdtCrew, NdtWeld } from '../../types/ndt-inspection';
import { useBackToList, useOpenRecord } from '../downhole/recordPage';
import { ConfirmDialog } from './NdtDialogs';
import { isNotFound, NDT_SECONDARY, NdtBadge, NdtEmpty, NdtLoading, NdtPanel, NdtRegister, NdtSpec, ndtDay, type NdtColumn } from './NdtBits';
import { NdtQualBadge, NdtRefusalNotice, NdtRepairRateCard } from './NdtJobBits';
import { NdtCancelJobDialog, NdtCrewDialog, NdtJobDialog, NdtWeldDialog } from './NdtJobDialogs';
import { NdtJobReportsPanel } from './NdtReportBits';
import { NdtJobTicketsPanel } from './NdtTicketBits';

const OPEN = ['scheduled', 'active'];

export default function JobDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/ndt-inspection/jobs');
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const query = useNdtJob(id);
  const weldsQuery = useNdtJobWelds(id);
  const update = useUpdateNdtJob();
  const removeCrew = useRemoveNdtCrew();
  const removeJob = useDeleteNdtJob();
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [addingCrew, setAddingCrew] = useState(false);
  const [addingWeld, setAddingWeld] = useState(false);
  const [refusal, setRefusal] = useState<NdtRefusal | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'crew'; crew: NdtCrew } | { kind: 'job' } | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const job = query.data?.data ?? null;
  const meta = query.data?.meta;
  const units = meta?.units ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const isOpen = !!job && OPEN.includes(job.status);
  const canDispatch = canDispatchNdt(user?.role) && !!job;
  const canWork = canWorkNdt(user?.role) && isOpen;
  const welds = weldsQuery.data?.data ?? [];
  const counts = job?.weldCounts;
  const completeBlocker = job?.status === 'active' ? ndtCompleteBlocker(counts) : null;

  const move = (status: 'active' | 'complete') => {
    if (!job) return;
    setRefusal(null);
    update.mutate(
      { id: job.id, body: { status } },
      {
        onSuccess: () => toast.success(status === 'active' ? `${job.jobNumber} started` : `${job.jobNumber} completed`),
        onError: (err) => setRefusal(ndtRefusal(err, 'The job could not be changed.')),
      },
    );
  };

  const doConfirm = () => {
    if (!confirm || !job) return;
    setConfirmError(null);
    if (confirm.kind === 'crew') {
      removeCrew.mutate(confirm.crew.id, {
        onSuccess: () => {
          toast.success('Removed from the crew');
          setConfirm(null);
        },
        onError: (err) => setConfirmError(ndtRefusal(err, 'The crew member could not be removed.').message),
      });
    } else {
      removeJob.mutate(job.id, {
        onSuccess: () => {
          toast.success(`${job.jobNumber} deleted`);
          navigate('/ndt-inspection/jobs', { replace: true });
        },
        onError: (err) => setConfirmError(ndtRefusal(err, 'The job could not be deleted.').message),
      });
    }
  };

  const weldColumns: NdtColumn<NdtWeld>[] = [
    { key: 'number', header: 'Weld', render: (w) => <span className="font-mono font-extrabold text-white">{w.weldNumber || '—'}</span> },
    { key: 'line', header: 'Line / spool', render: (w) => [w.lineRef, w.spool].filter(Boolean).join(' · ') || '—' },
    { key: 'size', header: units?.length ? `Dia × wall (${units.length})` : 'Dia × wall', render: (w) => `${formatNdtValue(w.diameter, null)} × ${formatNdtValue(w.wall, null)}` },
    { key: 'process', header: 'Process', render: (w) => w.processLabel || w.process || '—' },
    { key: 'welder', header: 'Welder', render: (w) => (w.welderName ? `${w.welderName}${w.welderStamp ? ` (${w.welderStamp})` : ''}` : '—') },
    { key: 'qual', header: 'Qualification', render: (w) => <NdtQualBadge status={w.welderQualification?.status} reasons={w.welderQualification?.reasons} compact /> },
    { key: 'exams', header: 'Exams', align: 'right', render: (w) => w.examCount },
    { key: 'standing', header: 'Standing', render: (w) => <NdtBadge skin={ndtWeldStandingSkin(w.standing)} value={w.standing} /> },
  ];

  return (
    <div data-testid="ndt-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection · Job"
        title={job?.jobNumber || (query.isLoading ? 'Job' : '—')}
        onBack={back}
        backLabel="Back"
        actions={
          canDispatch && isOpen ? (
            <div className="flex flex-wrap gap-2">
              {job!.status === 'scheduled' && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" disabled={update.isPending} onClick={() => move('active')}>
                  <Play className="size-4" aria-hidden="true" />
                  Start
                </button>
              )}
              {job!.status === 'active' && (
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={update.isPending || !!completeBlocker}
                  aria-describedby={completeBlocker ? 'ndt-complete-blocker' : undefined}
                  onClick={() => move('complete')}
                >
                  <CheckCircle2 className="size-4" aria-hidden="true" />
                  Complete
                </button>
              )}
              <button type="button" className="inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10" onClick={() => setEditing(true)}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className="inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10" onClick={() => setCancelling(true)}>
                <Ban className="size-4" aria-hidden="true" />
                Cancel job
              </button>
            </div>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Welds', sub: counts ? `${counts.accept} accepted` : undefined, tone: 'neutral', loading: query.isLoading, value: counts ? counts.total : '—' },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: job ? ndtJobStatusSkin(job.status).label : '—' },
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Crew', tone: 'neutral', loading: query.isLoading, value: job ? job.crewCount : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <NdtLoading label="Loading job" />
          ) : notFound || (!failure && !job) ? (
            <NdtPanel eyebrow="Job" title="Job">
              <NdtEmpty>This job was not found. It may have been deleted.</NdtEmpty>
            </NdtPanel>
          ) : failure || !job ? (
            <NdtPanel eyebrow="Job" title="Job">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this job" />
            </NdtPanel>
          ) : (
            <>
              <NdtRefusalNotice refusal={refusal} />
              {canDispatch && completeBlocker && (
                <p id="ndt-complete-blocker" data-testid="ndt-complete-blocker" className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
                  Complete is not available yet: {completeBlocker}
                </p>
              )}
              <NdtPanel eyebrow="Job" title="Details" actions={<NdtBadge skin={ndtJobStatusSkin(job.status)} value={job.status} />}>
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <NdtSpec label="Customer" value={job.customerName || '—'} />
                  <NdtSpec label="Line" value={job.lineRef || '—'} />
                  <NdtSpec label="Project" value={job.projectRef || '—'} />
                  <NdtSpec label="Location" value={job.locationName || '—'} />
                  <NdtSpec label={ndtLocationRefLabel(job.provinceState, job.currency ?? meta?.currency)} value={job.lsd || '—'} />
                  <NdtSpec label="Province / state" value={job.provinceState || '—'} />
                  <NdtSpec label="Acceptance code" value={job.acceptanceCodeLabel || '—'} />
                  <NdtSpec label="Field tickets" value={(job.ticketMode && NDT_TICKET_MODE_LABEL[job.ticketMode]) || '—'} />
                  <NdtSpec label="Scheduled" value={ndtDay(job.scheduledDate)} />
                  <NdtSpec label="Started" value={formatInstant(job.startedAt, meta?.timeZone) ?? '—'} />
                  <NdtSpec label="Completed" value={formatInstant(job.completedAt, meta?.timeZone) ?? '—'} />
                  <NdtSpec label="PO / AFE" value={[job.poNumber, job.afeNumber].filter(Boolean).join(' · ') || '—'} />
                  <NdtSpec label="External ref" value={[job.externalProjectRef, job.externalSource].filter(Boolean).join(' · ') || '—'} />
                  {/* Only a job booked from a quote has one; a payload without the key draws nothing. */}
                  {job.sourceQuote?.id && (
                    <NdtSpec
                      label="Quote"
                      value={
                        <Link to={`/quotes/${job.sourceQuote.id}`} data-testid="ndt-job-source-quote" className="font-bold text-fieldiq-text underline">
                          {job.sourceQuote.number || 'Open quote'}
                        </Link>
                      }
                    />
                  )}
                  <NdtSpec label="Lengths in" value={units?.length ?? '—'} />
                </dl>
                {/* pre-wrap: a job booked from a quote carries its scope one line per quote line (api create-ndt-job). */}
                {job.scope && <p data-testid="ndt-job-scope" className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{job.scope}</p>}
                {job.status === 'cancelled' && (
                  <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">Cancelled: {job.cancelReason || '—'}</p>
                )}
              </NdtPanel>

              <div className="grid gap-4 lg:grid-cols-2">
                <NdtPanel eyebrow="Welds" title="Weld standing" testId="ndt-weld-counts">
                  <dl className="grid grid-cols-3 gap-4 px-4 py-4 sm:grid-cols-6">
                    <NdtSpec label="Total" value={counts?.total ?? '—'} mono />
                    <NdtSpec label="Unexamined" value={counts?.unexamined ?? '—'} mono />
                    <NdtSpec label="Pending" value={counts?.pending ?? '—'} mono />
                    <NdtSpec label="Accepted" value={counts?.accept ?? '—'} mono />
                    <NdtSpec label="Repair" value={counts?.repair ?? '—'} mono />
                    <NdtSpec label="Cut out" value={counts?.cutout ?? '—'} mono />
                  </dl>
                </NdtPanel>
                <NdtRepairRateCard jobId={job.id} />
              </div>

              <NdtPanel
                eyebrow="Crew"
                title="Who is on this job"
                testId="ndt-crew-list"
                actions={
                  canDispatch && isOpen ? (
                    <button type="button" className={NDT_SECONDARY} onClick={() => setAddingCrew(true)}>
                      <Plus className="size-4" aria-hidden="true" />
                      Add to crew
                    </button>
                  ) : undefined
                }
              >
                {job.crew.length === 0 ? (
                  <NdtEmpty>Nobody is on this job's crew yet. Only the crew can record exams on it.</NdtEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {job.crew.map((m) => (
                      <li key={m.id} data-testid="ndt-crew-row" className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-extrabold text-white">{m.userName || '—'}</p>
                          <p className="text-xs text-fieldiq-muted">
                            {m.crewRoleLabel || m.crewRole}
                            {(m.startOn || m.endOn) && ` · ${ndtDay(m.startOn)} – ${ndtDay(m.endOn)}`}
                          </p>
                        </div>
                        {canDispatch && isOpen && (
                          <button
                            type="button"
                            className={NDT_SECONDARY}
                            aria-label={`Remove ${m.userName ?? 'crew member'}`}
                            onClick={() => {
                              setConfirmError(null);
                              setConfirm({ kind: 'crew', crew: m });
                            }}
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </NdtPanel>

              <NdtPanel
                eyebrow="Welds"
                title="Welds on this job"
                testId="ndt-job-welds"
                actions={
                  canWork ? (
                    <button type="button" className={NDT_SECONDARY} onClick={() => setAddingWeld(true)} disabled={!units}>
                      <Plus className="size-4" aria-hidden="true" />
                      Add weld
                    </button>
                  ) : undefined
                }
                footer={welds.length > 0 ? `${welds.length} weld${welds.length === 1 ? '' : 's'}` : undefined}
              >
                <NdtRegister
                  query={weldsQuery}
                  rows={welds}
                  columns={weldColumns}
                  rail={(w) => ndtWeldStandingSkin(w.standing).rail}
                  onOpen={(w) => openRecord(`/ndt-inspection/welds/${w.id}`)}
                  subject="this job's welds"
                  testId="ndt-job-weld"
                  minWidth="min-w-[1080px]"
                  empty={<NdtEmpty>No welds are recorded on this job.</NdtEmpty>}
                  card={(w) => (
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono text-sm font-extrabold text-white">{w.weldNumber}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{w.welderName || 'No welder'} · {w.examCount} exams</p>
                      </div>
                      <NdtBadge skin={ndtWeldStandingSkin(w.standing)} value={w.standing} />
                    </div>
                  )}
                />
              </NdtPanel>

              <NdtJobReportsPanel job={job} today={meta?.today ?? null} role={user?.role} />

              <NdtJobTicketsPanel job={job} today={meta?.today ?? null} canWork={canWorkNdt(user?.role)} canDispatch={canDispatch} />

              {canDispatch && job.status === 'scheduled' && counts?.total === 0 && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    className={NDT_SECONDARY}
                    onClick={() => {
                      setConfirmError(null);
                      setConfirm({ kind: 'job' });
                    }}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                    Delete job
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {job && canDispatch && <NdtJobDialog open={editing} onClose={() => setEditing(false)} job={job} meta={meta} />}
      {job && canDispatch && <NdtCancelJobDialog open={cancelling} onClose={() => setCancelling(false)} job={job} />}
      {job && canDispatch && <NdtCrewDialog open={addingCrew} onClose={() => setAddingCrew(false)} jobId={job.id} />}
      {job && canWork && (
        <NdtWeldDialog open={addingWeld} onClose={() => setAddingWeld(false)} jobId={job.id} units={units} onCreated={(wid) => openRecord(`/ndt-inspection/welds/${wid}`)} />
      )}
      {job && canDispatch && (
        <ConfirmDialog
          open={!!confirm}
          title={confirm?.kind === 'crew' ? 'Remove from crew' : 'Delete job'}
          body={confirm?.kind === 'crew' ? `Take ${confirm.crew.userName ?? 'this person'} off ${job.jobNumber}'s crew?` : `Delete ${job.jobNumber}? It has no welds.`}
          confirmLabel={confirm?.kind === 'crew' ? 'Remove' : 'Delete'}
          pending={removeCrew.isPending || removeJob.isPending}
          error={confirmError}
          onClose={() => setConfirm(null)}
          onConfirm={doConfirm}
        />
      )}
    </div>
  );
}
